import { useEffect, useRef } from 'react';
import { useLazyQuery } from '@apollo/react-hooks';
import GET_DEMOGRAPH_AUDIENCE_STATUS from '../GraphQl/Queries/GET_DEMOGRAPH_AUDIENCE_STATUS';

const POLL_INTERVAL = 10000; // 10 seconds

/**
 * Polls getDemographAudienceStatus for audiences that are in-progress
 * (Saving, Activating, Publishing) but not receiving live subscription updates.
 * When the status changes (synced=true or status transitions), triggers a refetch
 * of the audience list so the UI updates.
 */
const useDemographStatusPolling = (audienceDemographs, { onStatusChanged }) => {
  const intervalRef = useRef(null);

  const [fetchStatus] = useLazyQuery(GET_DEMOGRAPH_AUDIENCE_STATUS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const result = data?.getDemographAudienceStatus;
      if (result?.synced) {
        // DB was reconciled from internal-service — refetch to update UI
        if (onStatusChanged) onStatusChanged();
      }
    },
  });

  useEffect(() => {
    // audienceDemographs is the Apollo data object: { audienceDemographs: [...] }
    const audiences = audienceDemographs?.audienceDemographs || [];
    if (audiences.length === 0) return;

    // Find audiences that appear stuck in a saving/activating/publishing state
    const inProgressAudiences = audiences.filter((audience) => {
      // Has audienceId but save flags not set — likely stuck after save
      if (audience.audienceId && !audience.isPushedToS3MJ) return true;
      // Has prePublishedMetadata with ACTIVE status — job in progress
      const meta = typeof audience.prePublishedMetadata === 'string'
        ? (() => { try { return JSON.parse(audience.prePublishedMetadata); } catch { return null; } })()
        : audience.prePublishedMetadata;
      const parsed = meta?.set || meta;
      if (parsed?.status === 'ACTIVE') return true;
      return false;
    });

    if (inProgressAudiences.length === 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Poll each in-progress audience
    const poll = () => {
      inProgressAudiences.forEach((audience) => {
        fetchStatus({ variables: { audienceDemographId: audience.id } });
      });
    };

    // Poll immediately once, then on interval
    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [audienceDemographs, fetchStatus]);
};

export default useDemographStatusPolling;
